import React, { ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import {
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import { useSelector } from 'store';
import { t } from 'utils/getTranslations';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';
import { InAlertDialogContext } from '../ModalProvider';

/** A confirmation's copy as the kit alert dialog: a title naming what it acts on, a button saying what it does. */
export type AlertConfirmation = {
    titleId: string;
    titleValues?: Record<string, string | number>;
    /** The text under the title; the modal's `content` when not given. */
    description?: ReactNode;
    confirmId: string;
    confirmValues?: Record<string, string | number>;
    /** `danger`: the confirm button is the destructive one. */
    tone?: 'danger';
};

type ConfirmationModalProps = {
    titleId?: string;
    /** What the title interpolates: the name of what is deleted. */
    titleValues?: Record<string, string>;
    confirmId?: string;
    cancelId?: string;
    content: ReactNode;
    onCancel: () => void;
    onConfirm: () => void;
    extraButton?: JSX.Element;
    /** Opts in to the new design's confirmation: the modal stack draws it as the kit alert dialog with this copy. */
    alert?: AlertConfirmation;
};

function DisabledAwareExtraButton({ button }: { button: JSX.Element }) {
    const buttonsDisabled = useSelector((state) => state.buttons?.buttonsDisabled);
    return React.cloneElement(button, { disabled: buttonsDisabled || button.props.disabled });
}

/** The kit alert dialog's body; only it reads the store, so the classic modal renders as it did. */
function AlertConfirmationBody({
    alert,
    content,
    extraButton,
    onCancel,
    onConfirm,
}: {
    alert: AlertConfirmation;
    content: ReactNode;
    extraButton?: JSX.Element;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const buttonsDisabled = useSelector((state) => state.buttons?.buttonsDisabled);
    return (
        <>
            <AlertDialogHeader>
                <AlertDialogTitle>
                    <FormattedMessage id={alert.titleId} values={alert.titleValues} />
                </AlertDialogTitle>
            </AlertDialogHeader>
            {/* A div: the content may bring its own paragraphs. Out of the header, it sits the dialog's gap under the title. */}
            <AlertDialogDescription render={<div />} className="whitespace-pre-line">
                {alert.description ?? content}
            </AlertDialogDescription>
            <AlertDialogFooter>
                <Button variant="outline" onClick={onCancel} disabled={buttonsDisabled}>
                    <FormattedMessage id="cancel" />
                </Button>
                {extraButton}
                <Button
                    variant={alert.tone === 'danger' ? 'destructive' : 'secondary'}
                    onClick={onConfirm}
                    disabled={buttonsDisabled}
                >
                    <FormattedMessage id={alert.confirmId} values={alert.confirmValues} />
                </Button>
            </AlertDialogFooter>
        </>
    );
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = (props) => {
    const {
        titleId = 'confirmation',
        titleValues,
        confirmId = 'yes',
        cancelId = 'cancel',
        content,
        onCancel,
        onConfirm,
        extraButton,
        alert,
    } = props;
    const inAlertDialog = useContext(InAlertDialogContext);
    const disabledExtraButton = extraButton ? <DisabledAwareExtraButton button={extraButton} /> : undefined;
    if (inAlertDialog && alert) {
        return (
            <AlertConfirmationBody
                alert={alert}
                content={content}
                extraButton={disabledExtraButton}
                onCancel={onCancel}
                onConfirm={onConfirm}
            />
        );
    }
    return (
        <>
            <ModalHeader title={t(titleId, titleValues)} onCancel={onCancel} />
            <div className="overflow-y-auto p-6 whitespace-pre-line max-sm:px-4">{content}</div>
            <ModalFooter
                onCancel={onCancel}
                onSave={onConfirm}
                saveButtonTextId={confirmId}
                cancelButtonTextId={cancelId}
                extraButton={disabledExtraButton}
            />
        </>
    );
};

export default ConfirmationModal;
